// Web Speech API wrapper + voice command parser for ARIA Dictation.
// No content is ever sent to the server here — only browser-native STT.

export function isSpeechRecognitionSupported() {
  if (typeof window === "undefined") return false;
  return Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
}

export function createRecognizer({ onInterim, onFinal, onError, lang = "en-US" }) {
  const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!Ctor) throw new Error("SpeechRecognition not supported");
  const rec = new Ctor();
  rec.continuous = true;
  rec.interimResults = true;
  rec.lang = lang;

  rec.onresult = (event) => {
    let interim = "";
    let finalChunk = "";
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const r = event.results[i];
      if (r.isFinal) finalChunk += r[0].transcript;
      else interim += r[0].transcript;
    }
    if (finalChunk) onFinal?.(finalChunk);
    if (interim) onInterim?.(interim);
  };
  rec.onerror = (event) => onError?.(event.error || "unknown");
  return rec;
}

// Voice command parser — applies inline edits as the physician speaks.
// Commands are case-insensitive, surrounded by whitespace or sentence boundaries.
const VOICE_COMMANDS = [
  { match: /\s*\bnew paragraph\b\s*/gi, replace: "\n\n" },
  { match: /\s*\bnew line\b\s*/gi, replace: "\n" },
  { match: /\s*\bperiod\b\s*/gi, replace: ". " },
  { match: /\s*\bcomma\b\s*/gi, replace: ", " },
  { match: /\s*\bquestion mark\b\s*/gi, replace: "? " },
  { match: /\s*\bexclamation mark\b\s*/gi, replace: "! " },
  { match: /\s*\bcolon\b\s*/gi, replace: ": " },
  { match: /\s*\bsemicolon\b\s*/gi, replace: "; " },
];

export function applyVoiceCommands(text) {
  let out = text;
  for (const c of VOICE_COMMANDS) out = out.replace(c.match, c.replace);
  // Tidy double spaces
  return out.replace(/[ \t]+/g, " ").replace(/ +([.,;:?!])/g, "$1");
}

// Removes last sentence ("delete that") or last phrase ("scratch that")
// Operates on the already-committed text buffer.
export function applyRemoveCommand(buffer, command) {
  if (!buffer) return buffer;
  if (command === "delete-sentence") {
    // Find last sentence boundary
    const m = buffer.match(/(.*?)([.!?]\s*)([^.!?]*)$/);
    if (m) return (m[1] + m[2]).trimEnd();
    return "";
  }
  if (command === "scratch-phrase") {
    // Drop last comma- or space-delimited phrase
    const idx = Math.max(buffer.lastIndexOf(","), buffer.lastIndexOf(" "));
    return idx > 0 ? buffer.slice(0, idx).trimEnd() : "";
  }
  return buffer;
}

// Detect destructive commands in the just-spoken final chunk and act on the buffer.
// Returns { newBuffer, addition } — addition is the text to append after commands are stripped.
export function processFinalChunk(buffer, finalChunk) {
  let addition = finalChunk;
  let newBuffer = buffer;

  if (/\bdelete that\b/i.test(addition)) {
    newBuffer = applyRemoveCommand(newBuffer, "delete-sentence");
    addition = addition.replace(/\s*\bdelete that\b\s*/gi, "");
  }
  if (/\bscratch that\b/i.test(addition)) {
    newBuffer = applyRemoveCommand(newBuffer, "scratch-phrase");
    addition = addition.replace(/\s*\bscratch that\b\s*/gi, "");
  }
  addition = applyVoiceCommands(addition);
  return { newBuffer, addition };
}

// DOCX export — generates a minimal valid .docx via the Office Open XML spec
// (no extra dependency needed). Returns a Blob the caller can save.
export function buildDocxBlob(title, body) {
  // Escape XML
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const paragraphs = body
    .split(/\n+/)
    .map((line) => {
      const isHeading = /^\*\*(.+)\*\*$/.test(line.trim());
      const text = esc(line.replace(/^\*\*|\*\*$/g, ""));
      if (isHeading) {
        return `<w:p><w:pPr><w:pStyle w:val="Heading2"/></w:pPr><w:r><w:rPr><w:b/><w:sz w:val="28"/></w:rPr><w:t xml:space="preserve">${text}</w:t></w:r></w:p>`;
      }
      return `<w:p><w:r><w:t xml:space="preserve">${text}</w:t></w:r></w:p>`;
    })
    .join("");
  const documentXml =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">` +
    `<w:body>` +
    `<w:p><w:r><w:rPr><w:b/><w:sz w:val="36"/></w:rPr><w:t>${esc(title)}</w:t></w:r></w:p>` +
    paragraphs +
    `<w:sectPr/></w:body></w:document>`;
  const contentTypes =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
    `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
    `<Default Extension="xml" ContentType="application/xml"/>` +
    `<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>` +
    `</Types>`;
  const rels =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>` +
    `</Relationships>`;
  // Build a zip manually using the minimal "store" (no compression) format.
  // We use the browser's built-in Blob + a tiny ZIP writer.
  return zipStore([
    { name: "[Content_Types].xml", data: contentTypes },
    { name: "_rels/.rels", data: rels },
    { name: "word/document.xml", data: documentXml },
  ]);
}

// Minimal stored-mode (no-compression) ZIP writer. Browser-safe.
function zipStore(files) {
  const enc = new TextEncoder();
  const chunks = [];
  const centralDir = [];
  let offset = 0;

  function crc32(buf) {
    let c;
    const table = (zipStore._table = zipStore._table || (() => {
      const t = new Uint32Array(256);
      for (let n = 0; n < 256; n++) {
        c = n;
        for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        t[n] = c >>> 0;
      }
      return t;
    })());
    c = 0xffffffff;
    for (let i = 0; i < buf.length; i++) c = table[(c ^ buf[i]) & 0xff] ^ (c >>> 8);
    return (c ^ 0xffffffff) >>> 0;
  }

  for (const f of files) {
    const nameBuf = enc.encode(f.name);
    const dataBuf = enc.encode(f.data);
    const crc = crc32(dataBuf);
    const size = dataBuf.length;
    const local = new Uint8Array(30 + nameBuf.length);
    const dv = new DataView(local.buffer);
    dv.setUint32(0, 0x04034b50, true);
    dv.setUint16(4, 20, true);   // version needed
    dv.setUint16(6, 0, true);    // flags
    dv.setUint16(8, 0, true);    // method = store
    dv.setUint16(10, 0, true);   // time
    dv.setUint16(12, 0, true);   // date
    dv.setUint32(14, crc, true);
    dv.setUint32(18, size, true); // compressed size
    dv.setUint32(22, size, true); // uncompressed size
    dv.setUint16(26, nameBuf.length, true);
    dv.setUint16(28, 0, true);   // extra
    local.set(nameBuf, 30);
    chunks.push(local, dataBuf);

    const central = new Uint8Array(46 + nameBuf.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true);
    cv.setUint16(6, 20, true);
    cv.setUint16(8, 0, true);
    cv.setUint16(10, 0, true);
    cv.setUint16(12, 0, true);
    cv.setUint16(14, 0, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, size, true);
    cv.setUint32(24, size, true);
    cv.setUint16(28, nameBuf.length, true);
    cv.setUint16(30, 0, true);
    cv.setUint16(32, 0, true);
    cv.setUint16(34, 0, true);
    cv.setUint16(36, 0, true);
    cv.setUint32(38, 0, true);
    cv.setUint32(42, offset, true);
    central.set(nameBuf, 46);
    centralDir.push(central);

    offset += local.length + dataBuf.length;
  }

  const centralStart = offset;
  let centralSize = 0;
  for (const c of centralDir) centralSize += c.length;

  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(4, 0, true);
  ev.setUint16(6, 0, true);
  ev.setUint16(8, files.length, true);
  ev.setUint16(10, files.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, centralStart, true);
  ev.setUint16(20, 0, true);

  return new Blob([...chunks, ...centralDir, end], {
    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
}
